/**
 * How a note is ADDRESSED — the id, and the local-cache store name.
 *
 * Deliberately dependency-free (no pdf-lib, no Tiptap, no React) so it is safe
 * to import from a React Native bundle, a DOM/WebView bundle, and the web app
 * alike. Do NOT re-export it from `./index`, which pulls in the PDF generator.
 *
 * These strings are cross-client contracts: get one wrong and the client opens
 * a DIFFERENT row / store than its peers, with no error — the note just reads
 * as empty. That is why they live here instead of being spelled out at each
 * call site (they were spelled out in seven).
 */

/** The one note every deck has. v1 is single-note; reserved for a multi-note future. */
export const DEFAULT_NOTE_ID = 'default';

/**
 * Which scope a note's local cache belongs to. A personal note is cached per
 * USER; a study-group note per GROUP, so two members on one device never share
 * a store and a user's own copy never collides with the group's.
 */
export type NoteIdbScope =
  | { kind: 'personal'; userId: string; deckKey: string; noteId?: string }
  | { kind: 'group'; groupId: string; sgDeckId: string; noteId?: string };

/**
 * IndexedDB store name for a note's local cache.
 *
 * NOTE — this is the format the MOBILE clients have always written, and it
 * differs from `noteIdbStoreName` in `sync-client/yjs/note-doc-registry.ts`,
 * which keys personal notes by `room` and has no group variant at all. The two
 * only agree for personal notes (where `room === userId`). Renaming either one
 * orphans every already-cached note on device, so they are kept as-is and
 * reconciled deliberately rather than by accident; this helper exists so that
 * when they ARE reconciled it is a one-line change instead of seven.
 */
export function noteIdbStoreName(scope: NoteIdbScope): string {
  const noteId = scope.noteId ?? DEFAULT_NOTE_ID;
  return scope.kind === 'group'
    ? `flashka-sg-note:${scope.groupId}:${scope.sgDeckId}:${noteId}`
    : `flashka-note:${scope.userId}:${scope.deckKey}:${noteId}`;
}
